/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Checkbox, FormControlLabel, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { invalidateQueryKeys } from "../../hooks/mutations/utils/invalidateQueries";
import { cityMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/cityMutationKeys";
import { LoadingSpinner } from "../../components/loading_spinner";
import { boundsAround, radiusOf } from "./boundsAround";
import {
  StyledAddButton,
  StyledCancelButton,
  StyledFormFieldText,
  StyledTextField,
} from "../restaurant_groups/styles";
import {
  CityDialog,
  CityDialogActions,
  CityDialogContent,
  CityDialogHeader,
  CityDialogTitle,
  CityFieldGrid,
  CityFillButton,
  CityHelperText,
  CitySectionTitle,
} from "./styles";

const LABELS = {
  name: "Name",
  center_lat: "Center latitude",
  center_lng: "Center longitude",
  north: "North",
  south: "South",
  east: "East",
  west: "West",
  is_default: "Default",
}

// Max absolute value per coordinate field.
const COORDINATE_LIMITS = { center_lat: 90, center_lng: 180, north: 90, south: 90, east: 180, west: 180 }
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/
const NAME_MAX_LENGTH = 100
const COORDINATE_MAX_LENGTH = 20
const BOUND_FIELDS = ['north', 'south', 'east', 'west']
const DEFAULT_RADIUS_KM = '25'
const RADIUS_MIN_KM = 0.1
const RADIUS_MAX_KM = 500
const FIX_FIELDS_MESSAGE = 'Please correct the highlighted fields.'

// An existing city's radius is worked out from its saved bounds (see `radiusOf`).
const initialRadius = (city) => (city?.id ? radiusOf(city) : DEFAULT_RADIUS_KM)

// Plain decimals only: the API's tiny numbers would otherwise print as "1e-7".
const toInput = (value) => {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /e/i.test(text) ? Number(value).toFixed(10).replace(/\.?0+$/, '') : text
}

const initialValues = (city) => ({
  name: city?.name || '',
  ...Object.fromEntries(Object.keys(COORDINATE_LIMITS).map((field) => [field, toInput(city?.[field])])),
  is_default: Boolean(city?.is_default),
})

// Message for a blank, non-numeric or out-of-range coordinate; null when it is valid.
const coordinateError = (field, value) => {
  const raw = value.trim()
  const limit = COORDINATE_LIMITS[field]
  if (!raw) return `${LABELS[field]} can't be blank`
  if (!NUMBER_PATTERN.test(raw)) return `${LABELS[field]} must be a number`
  if (Math.abs(Number(raw)) > limit) return `${LABELS[field]} must be between -${limit} and ${limit}`
  return null
}

// The radius only sizes the auto-filled bounds; it is never saved.
const radiusError = (value) => {
  const raw = value.trim()
  const valid = NUMBER_PATTERN.test(raw) && Number(raw) >= RADIUS_MIN_KM && Number(raw) <= RADIUS_MAX_KM
  return valid ? null : `Radius must be a number from ${RADIUS_MIN_KM} to ${RADIUS_MAX_KM}`
}

// Same rules as the server, so obvious mistakes never reach it. Number format is stricter (no "+1" or ".5").
const validate = (values) => {
  const errors = {}
  if (!values.name.trim()) {
    errors.name = "Name can't be blank"
  }

  Object.keys(COORDINATE_LIMITS).forEach((field) => {
    const message = coordinateError(field, values[field])
    if (message) errors[field] = message
  })

  const valid = (...fields) => fields.every((field) => !errors[field])
  const num = (field) => Number(values[field])

  if (valid('north', 'south') && num('north') <= num('south')) {
    errors.north = 'North must be greater than south'
  }
  if (valid('east', 'west') && num('east') <= num('west')) {
    errors.east = 'East must be greater than west'
  }
  if (valid('center_lat', 'north', 'south') && (num('center_lat') < num('south') || num('center_lat') > num('north'))) {
    errors.center_lat = 'Center latitude must be between south and north'
  }
  if (valid('center_lng', 'east', 'west') && (num('center_lng') < num('west') || num('center_lng') > num('east'))) {
    errors.center_lng = 'Center longitude must be between west and east'
  }

  return errors
}

// Splits server errors ({ field: [messages] }) into inline field messages and the rest.
const parseServerErrors = (serverErrors) => {
  const fieldErrors = {}
  const otherErrors = []
  Object.entries(serverErrors || {}).forEach(([field, messages]) => {
    const message = [].concat(messages)[0]
    if (!message) return

    if (LABELS[field]) {
      fieldErrors[field] = `${LABELS[field]} ${message}`
    } else {
      otherErrors.push(field === 'base' ? message : `${field} ${message}`)
    }
  })
  return { fieldErrors, otherErrors }
}

const hasFieldErrors = (error) => {
  const serverErrors = error?.response?.data?.errors
  return Boolean(serverErrors) && typeof serverErrors === 'object' && !Array.isArray(serverErrors)
    && Object.keys(serverErrors).length > 0
}

// Create or edit a city. Edit mode when `city` has an id.
export const CityFormDialog = ({ open, city, onClose }) => {
  const isEdit = Boolean(city?.id)
  // The current default can only be replaced, never turned off.
  const isCurrentDefault = isEdit && Boolean(city?.is_default)
  const [values, setValues] = useState(() => initialValues(city))
  const [errors, setErrors] = useState({})
  const [formErrors, setFormErrors] = useState([])
  const [radiusKm, setRadiusKm] = useState(() => initialRadius(city))
  const [radiusMessage, setRadiusMessage] = useState(null)
  // Radius of auto-filled bounds; null when bounds were typed or saved.
  const [autoFilledRadius, setAutoFilledRadius] = useState(null)
  // Radius the bounds were last built with (25 on a new city, '' when unknown); a new radius enables "Fill from center".
  const [lastFillRadius, setLastFillRadius] = useState(() => initialRadius(city))
  const queryClient = useQueryClient()

  useEffect(() => {
    if (open) {
      setValues(initialValues(city))
      setErrors({})
      setFormErrors([])
      setRadiusKm(initialRadius(city))
      setRadiusMessage(null)
      setAutoFilledRadius(null)
      setLastFillRadius(initialRadius(city))
    }
  }, [open, city])

  const { mutate, isPending } = useOtMutation({
    mutationFn: (payload) => (isEdit
      ? api.put(`/v1/admin/cities/${city.id}`, payload)
      : api.post('/v1/admin/cities', payload)),
    invalidateKeys: isEdit ? cityMutationKeyGroups.update : cityMutationKeyGroups.create,
    successMessage: isEdit ? 'City has been updated successfully' : 'City has been created successfully',
    // Validation details show in the form; other failures toast the server's message.
    errorMessage: ({ error }) => (hasFieldErrors(error) ? FIX_FIELDS_MESSAGE : undefined),
    onSuccess: () => onClose(),
    onError: (error) => {
      // Deleted elsewhere while open: refresh the list and close.
      if (isEdit && error?.response?.status === 404) {
        invalidateQueryKeys(queryClient, cityMutationKeyGroups.update)
        onClose()
        return
      }
      if (!hasFieldErrors(error)) return

      const { fieldErrors, otherErrors } = parseServerErrors(error.response.data.errors)
      setErrors(fieldErrors)
      setFormErrors(otherErrors)
    },
  })

  const handleChange = (field) => (event) => {
    const value = field === 'is_default' ? event.target.checked : event.target.value
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current))
    // A hand-edited bound is never overwritten by auto-fill.
    if (BOUND_FIELDS.includes(field)) setAutoFilledRadius(null)
  }

  const hasTypedBounds = autoFilledRadius === null && BOUND_FIELDS.some((field) => values[field].trim())
  const centerValid = !coordinateError('center_lat', values.center_lat) && !coordinateError('center_lng', values.center_lng)
  const canFillFromCenter = centerValid && !radiusError(radiusKm) && Number(radiusKm) !== Number(lastFillRadius)

  // Fills the bounds from the center and radius. Typed bounds are replaced only when forced.
  const fillBounds = (radius = radiusKm, { force = false } = {}) => {
    if ((hasTypedBounds && !force) || !centerValid || radiusError(radius)) {
      return
    }

    setValues((current) => ({
      ...current,
      ...boundsAround(Number(values.center_lat), Number(values.center_lng), Number(radius)),
    }))
    setErrors((current) => ({ ...current, ...Object.fromEntries(BOUND_FIELDS.map((field) => [field, undefined])) }))
    setAutoFilledRadius(radius.trim())
    setLastFillRadius(radius.trim())
  }

  const handleRadiusBlur = () => {
    setRadiusMessage(radiusKm.trim() ? radiusError(radiusKm) : null)
    fillBounds()
  }

  const handleSubmit = () => {
    if (isPending) return

    const nextErrors = validate(values)
    setErrors(nextErrors)
    setFormErrors([])
    if (Object.keys(nextErrors).length > 0) return

    // Coordinates go as trimmed strings so the server stores the exact decimals typed.
    const payload = {
      name: values.name.trim(),
      ...Object.fromEntries(Object.keys(COORDINATE_LIMITS).map((field) => [field, values[field].trim()])),
    }
    // On edit, is_default goes only when changed, so a form opened before another admin
    // moved the default can't move it back.
    if (!isEdit || values.is_default !== Boolean(city.is_default)) payload.is_default = values.is_default

    mutate(payload)
  }

  // Closing mid-save would hide the result, so wait for the request.
  const handleClose = () => {
    if (!isPending) onClose()
  }

  const renderField = (field, { maxLength = COORDINATE_MAX_LENGTH, placeholder, onBlur } = {}) => (
    <div>
      <StyledFormFieldText>{LABELS[field]}<span style={{ color: 'red' }}>*</span></StyledFormFieldText>
      <StyledTextField
        fullWidth
        size="small"
        value={values[field]}
        placeholder={placeholder}
        onChange={handleChange(field)}
        onBlur={onBlur}
        error={Boolean(errors[field])}
        helperText={errors[field]}
        inputProps={{
          maxLength,
          inputMode: field === 'name' ? 'text' : 'decimal',
          'data-testid': `city-${field}-input`,
        }}
      />
    </div>
  )

  return (
    <CityDialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-testid="city-form-dialog">
      <CityDialogHeader>
        <CityDialogTitle>{isEdit ? 'Edit City' : 'Create City'}</CityDialogTitle>
        <IconButton onClick={handleClose} aria-label="Close city form">
          <CloseIcon sx={{ color: '#707070' }} />
        </IconButton>
      </CityDialogHeader>

      <CityDialogContent>
        {formErrors.length > 0 && (
          <Alert severity="error" data-testid="city-form-error">{formErrors.join('. ')}</Alert>
        )}

        {renderField('name', { maxLength: NAME_MAX_LENGTH })}

        <div>
          <CitySectionTitle>Map center</CitySectionTitle>
          <CityFieldGrid sx={{ marginTop: '10px' }}>
            {renderField('center_lat', { placeholder: 'e.g. 30.2672', onBlur: () => fillBounds() })}
            {renderField('center_lng', { placeholder: 'e.g. -97.7431', onBlur: () => fillBounds() })}
          </CityFieldGrid>
        </div>

        <div>
          <CitySectionTitle>Bounds</CitySectionTitle>
          <CityHelperText data-testid="city-bounds-help">
            {autoFilledRadius && `Filled with a ${autoFilledRadius} km box around the center. Adjust if needed.`}
            {hasTypedBounds && 'These bounds are kept when the center or radius changes. Change the radius and click "Fill from center" to replace them.'}
            {!autoFilledRadius && !hasTypedBounds
              && 'Empty bounds fill in from the center and radius. North must be above south, and east above west.'}
          </CityHelperText>
          <CityFieldGrid>
            <div>
              <StyledFormFieldText>Radius (km)</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={radiusKm}
                onChange={(event) => {
                  setRadiusKm(event.target.value)
                  setRadiusMessage(null)
                }}
                onBlur={handleRadiusBlur}
                error={Boolean(radiusMessage)}
                helperText={radiusMessage || (radiusKm.trim()
                  ? 'Distance from the center to each edge.'
                  : 'Enter a radius to fill the bounds from the center.')}
                inputProps={{ maxLength: 6, inputMode: 'decimal', 'data-testid': 'city-radius-input' }}
              />
            </div>
            <div>
              {/* Empty label keeps the button level with the radius input. */}
              <StyledFormFieldText aria-hidden>&nbsp;</StyledFormFieldText>
              <CityFillButton
                variant="outlined"
                onClick={() => fillBounds(radiusKm, { force: true })}
                disabled={!canFillFromCenter}
                data-testid="city-fill-bounds-button"
              >
                Fill from center
              </CityFillButton>
            </div>
          </CityFieldGrid>
          <CityFieldGrid sx={{ marginTop: '16px' }}>
            {renderField('north')}
            {renderField('south')}
            {renderField('east')}
            {renderField('west')}
          </CityFieldGrid>
        </div>

        <div>
          <FormControlLabel
            control={(
              <Checkbox
                checked={values.is_default}
                disabled={isCurrentDefault}
                onChange={handleChange('is_default')}
                inputProps={{ 'data-testid': 'city-is_default-input' }}
              />
            )}
            label="Default city"
          />
          <CityHelperText error={Boolean(errors.is_default)}>
            {errors.is_default || (isCurrentDefault
              ? 'This is the default city. To change it, make another city the default.'
              : 'The mobile app opens on the default city. This replaces the current default.')}
          </CityHelperText>
        </div>
      </CityDialogContent>

      <CityDialogActions>
        <StyledCancelButton variant="outlined" onClick={handleClose} disabled={isPending} data-testid="city-cancel-button">
          Cancel
        </StyledCancelButton>
        <StyledAddButton
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending}
          sx={{ padding: '6px 30px !important' }}
          data-testid="city-submit-button"
        >
          {isPending ? <LoadingSpinner isLoading={true} /> : (isEdit ? 'Save' : 'Create')}
        </StyledAddButton>
      </CityDialogActions>
    </CityDialog>
  )
}
